import React, { useState } from 'react';
import Button from '../Button';
import { usePerformanceTest } from '../../hooks/usePerformanceTest';
import { ApiResultBlock } from '../ListingPerformance/ApiResultBlock';

const GAME_OPTIONS = [
  { value: 'gta5', label: 'GTA 5' },
  { value: 'rust', label: 'Rust' },
  { value: 'csgo', label: 'CS:GO' },
  { value: 'valorant', label: 'Valorant' },
  { value: 'fortnite', label: 'Fortnite' },
  { value: 'rdr2', label: 'Red Dead Redemption 2' },
  { value: 'cyberpunk', label: 'Cyberpunk 2077' },
  { value: 'warzone', label: 'Call of Duty: Warzone' },
];

const APP_OPTIONS = [
  { value: 'photoshop', label: 'Adobe Photoshop' },
  { value: 'premiere', label: 'Adobe Premiere Pro' },
  { value: 'autocad', label: 'AutoCAD' },
  { value: 'blender', label: 'Blender' },
  { value: 'visualstudio', label: 'Visual Studio' },
  { value: 'office', label: 'Microsoft Office' },
];

export interface ListingAiPerformanceSectionProps {
  specs: Record<string, string>;
}

/**
 * AI-powered game FPS and software compatibility testing for desktop/laptop listings.
 */
const ListingAiPerformanceSection: React.FC<ListingAiPerformanceSectionProps> = ({ specs }) => {
  const [selectedGame, setSelectedGame] = useState('');
  const [selectedApp, setSelectedApp] = useState('');
  const performanceTest = usePerformanceTest({ specs });

  const handleTestGame = () => {
    if (!selectedGame) {
      alert('Lütfen önce bir oyun seçiniz.');
      return;
    }
    performanceTest.fps.run(selectedGame);
  };

  const handleTestApp = () => {
    if (!selectedApp) {
      alert('Lütfen önce bir uygulama seçiniz.');
      return;
    }
    performanceTest.compatibility.run(selectedApp);
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="card-title mb-4">
          <i className="fas fa-robot me-2 text-primary" aria-hidden="true" />
          Yapay Zeka ile Sistemi Test Et
        </h5>

        <div className="mb-4">
          <label className="form-label" htmlFor="aiGameSelector">
            Oyun Seçin
          </label>
          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 sb-performance-actions">
            <select
              className="form-select flex-grow-1 min-w-0"
              id="aiGameSelector"
              value={selectedGame}
              onChange={(e) => setSelectedGame(e.target.value)}
            >
              <option value="">Oyun seçiniz...</option>
              {GAME_OPTIONS.map((game) => (
                <option key={game.value} value={game.value}>
                  {game.label}
                </option>
              ))}
            </select>
            <div className="d-grid d-sm-block flex-shrink-0">
              <Button
                type="button"
                className="btn btn-primary"
                onClick={handleTestGame}
                disabled={performanceTest.fps.loading}
              >
                {performanceTest.fps.loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                ) : (
                  <i className="fas fa-play-circle me-2" aria-hidden="true" />
                )}
                Test Et
              </Button>
            </div>
          </div>
          <ApiResultBlock
            loading={performanceTest.fps.loading}
            result={performanceTest.fps.result}
            error={performanceTest.fps.error}
          />
        </div>

        <div>
          <label className="form-label" htmlFor="aiAppSelector">
            İş Uygulaması Seçin
          </label>
          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 sb-performance-actions">
            <select
              className="form-select flex-grow-1 min-w-0"
              id="aiAppSelector"
              value={selectedApp}
              onChange={(e) => setSelectedApp(e.target.value)}
            >
              <option value="">Uygulama seçiniz...</option>
              {APP_OPTIONS.map((app) => (
                <option key={app.value} value={app.value}>
                  {app.label}
                </option>
              ))}
            </select>
            <div className="d-grid d-sm-block flex-shrink-0">
              <Button
                type="button"
                className="btn btn-primary"
                onClick={handleTestApp}
                disabled={performanceTest.compatibility.loading}
              >
                {performanceTest.compatibility.loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                ) : (
                  <i className="fas fa-play-circle me-2" aria-hidden="true" />
                )}
                Test Et
              </Button>
            </div>
          </div>
          <ApiResultBlock
            loading={performanceTest.compatibility.loading}
            result={performanceTest.compatibility.result}
            error={performanceTest.compatibility.error}
          />
        </div>
      </div>
    </div>
  );
};

export default ListingAiPerformanceSection;
